/**
 * LocalTerrainSource - client-side terrain generation for offline / local play.
 *
 * Mirrors the server's generation pipeline (ChunkProvider + MapTileProvider +
 * SurfaceColumnProvider) using the SAME shared `TerrainGenerator` and helpers,
 * so a local world is identical to a server world for a given seed. Produces the
 * exact network response shapes (VoxelChunkData / MapTileResponse /
 * SurfaceColumnResponse) so VoxelWorld can ingest them through its existing
 * receive* methods with no other changes.
 *
 * No persistence yet (a fresh world each session) — that is a later addition.
 */

import type { WorldGenConfig } from '@worldify/shared';
import {
  TerrainGenerator,
  createMapTile,
  tilePixelIndex,
  updateTileFromChunk,
  chunkHasContent,
  scanMultiChunkColumn,
  getChunkRangeFromHeights,
  packChunkKey,
  CHUNK_SIZE,
  VOXEL_SCALE,
  MAP_TILE_SIZE,
  type VoxelChunkData,
  type MapTileResponse,
  type SurfaceColumnResponse,
  type SurfaceColumnChunk,
} from '@worldify/shared';

/** Matches SurfaceColumnProvider on the server. */
const CHUNKS_BELOW_SURFACE = 0;
const MAX_CHUNKS_ABOVE = 4;

/**
 * Coarse (LOD ≥ 1) chunk payload. Client-local (never crosses the wire protocol — coarse chunks
 * are always generated locally, even in server-chunk mode).
 */
export interface VoxelLodChunkData {
  chunkX: number;
  chunkY: number;
  chunkZ: number;
  lod: number;
  /** Raw voxel data (32×32×32 uint32), sampled at 2^lod× stride */
  voxelData: Uint32Array;
  /** True when the producer already ran the standalone coarse lighting (the terrain worker does). */
  lit?: boolean;
}

/** Vertical chunk range for a coarse column (in coarse chunk indices) — the LOD ring streamer's
 *  sky/underground clip, mirroring what LOD-0 tiles provide via columnInfo. */
export interface LodColumnResponse {
  cx: number;
  cz: number;
  lod: number;
  minCy: number;
  maxCy: number;
}

/**
 * A coarse column's clip AND the chunks inside it — the coarse mirror of `generateColumn`, and for
 * the same reason.
 *
 * A coarse column used to cost TWO SERIALIZED ROUND TRIPS through the shared worker pool: a probe
 * to learn the vertical clip, then — a pass later, from the back of the queue — the chunk requests
 * it unlocked. The base level pays ONE for the same volume, because its tile request is really a
 * column bundle. Measured at cold start, that asymmetry was the largest single term in "coarse is
 * 25 s behind fine": probes were 40-48% of every coarse job submitted, and the first coarse CHUNK
 * job did not reach the pool until job #24 (t 6.5 s) against the first base bundle at job #0
 * (t 2.4 s). One job answering both questions removes a whole queue traversal per column and frees
 * the probe slots for columns nobody has looked at yet.
 */
export interface LodColumnBundleResponse extends LodColumnResponse {
  /** Coarse chunks in this column, clipped to the requested cy window (see generateLodColumnBundle).
   *  `lit` as on VoxelLodChunkData. */
  chunks: Array<{ cy: number; voxelData: Uint32Array; lit?: boolean }>;
}

export class LocalTerrainSource {
  private readonly gen: TerrainGenerator;
  /** Cache raw generated chunk data so tile scanning and chunk requests agree. Packed integer key
   *  (±2^16 chunk range) — avoids a per-lookup string build + Map<string> hash on this hot path. */
  private readonly cache = new Map<number, Uint32Array>();

  /**
   * ONE CONFIG. A world hands over its node tree and the generator compiles it; without one (the
   * server path, which is seeded from WELCOME) the engine defaults stand.
   */
  constructor(seed: number, genConfig?: WorldGenConfig) {
    this.gen = genConfig
      ? TerrainGenerator.fromGenConfig({ ...genConfig, seed })
      : new TerrainGenerator({ seed });
  }

  private rawChunk(cx: number, cy: number, cz: number): Uint32Array {
    const key = packChunkKey(cx, cy, cz);
    let data = this.cache.get(key);
    if (!data) {
      data = this.gen.generateChunk(cx, cy, cz);
      this.cache.set(key, data);
    }
    return data;
  }

  /** Fill a fresh tile with terrain-baseline surface heights/materials. */
  private baselineTile(tx: number, tz: number) {
    const tile = createMapTile(tx, tz);
    const worldX0 = tx * CHUNK_SIZE * VOXEL_SCALE;
    const worldZ0 = tz * CHUNK_SIZE * VOXEL_SCALE;
    for (let lz = 0; lz < MAP_TILE_SIZE; lz++) {
      for (let lx = 0; lx < MAP_TILE_SIZE; lx++) {
        const { height, material } = this.gen.sampleSurface(
          worldX0 + lx * VOXEL_SCALE,
          worldZ0 + lz * VOXEL_SCALE,
        );
        const i = tilePixelIndex(lx, lz);
        tile.heights[i] = height;
        tile.materials[i] = material;
      }
    }
    return tile;
  }

  /** A spawn point on the path network (world metres), or null in a world without paths. */
  findPathSpawn(): { x: number; y: number; z: number } | null {
    return this.gen.findPathSpawn();
  }

  /** Generate a single chunk (equivalent of a server VOXEL_CHUNK_DATA response). */
  generateChunk(cx: number, cy: number, cz: number): VoxelChunkData {
    return {
      chunkX: cx,
      chunkY: cy,
      chunkZ: cz,
      lastBuildSeq: 0,
      voxelData: this.rawChunk(cx, cy, cz),
    };
  }

  /**
   * Build the surface column for a tile: the surface chunks + a tile whose heights are corrected to
   * include stamps (tree canopies, buildings) that rise above the bare-terrain surface. Shared by
   * generateTile and generateColumn so BOTH report the true (stamp-inclusive) surface height — the
   * client's vertical load cap comes from these heights, so a terrain-only height would clip stamp
   * tops at the chunk boundary.
   */
  private buildSurfaceColumn(tx: number, tz: number): { tile: ReturnType<typeof createMapTile>; chunks: SurfaceColumnChunk[] } {
    const tile = this.baselineTile(tx, tz);

    const { minCy: terrainMinCy, maxCy: terrainMaxCy } = getChunkRangeFromHeights(tile.heights);
    const minCy = terrainMinCy - CHUNKS_BELOW_SURFACE;

    const chunks: SurfaceColumnChunk[] = [];
    const chunkDatas: Array<{ cy: number; data: Uint32Array }> = [];

    for (let cy = minCy; cy <= terrainMaxCy + MAX_CHUNKS_ABOVE; cy++) {
      const data = this.rawChunk(tx, cy, tz);
      const hasContent = chunkHasContent(data);
      // Always include terrain chunks; above terrain, include chunks with content (canopies,
      // buildings), stopping at the first empty chunk above the terrain top.
      if (cy <= terrainMaxCy || hasContent) {
        chunkDatas.push({ cy, data });
        chunks.push({ chunkY: cy, lastBuildSeq: 0, voxelData: data });
      }
      if (cy > terrainMaxCy && !hasContent) break;
    }

    // Correct the tile's surface heights to include stamps spanning chunks (canopy / building tops).
    for (const c of chunkDatas) {
      updateTileFromChunk(
        tile,
        { cx: tx, cy: c.cy, cz: tz, data: c.data },
        (lx, lz) => scanMultiChunkColumn(chunkDatas, lx, lz),
      );
    }

    return { tile, chunks };
  }

  /**
   * Generate a coarse (LOD ≥ 1) chunk. NOT cached: each coarse chunk is requested once and held by
   * VoxelWorld; tile scans never read them, so the rawChunk cache (and its lod-less packed-int
   * keys) stays LOD-0-only by construction.
   */
  generateLodChunk(cx: number, cy: number, cz: number, lod: number): VoxelLodChunkData {
    return {
      chunkX: cx,
      chunkY: cy,
      chunkZ: cz,
      lod,
      voxelData: this.gen.generateChunkLOD(cx, cy, cz, lod),
    };
  }

  /** Vertical chunk range for a coarse column footprint (see TerrainGenerator.generateLodColumn). */
  generateLodColumn(cx: number, cz: number, lod: number): LodColumnResponse {
    const { minCy, maxCy } = this.gen.generateLodColumn(cx, cz, lod);
    return { cx, cz, lod, minCy, maxCy };
  }

  /**
   * The clip AND the chunks it admits, in one job — see LodColumnBundleResponse for why.
   *
   * BOUNDED IN BOTH DIRECTIONS, because a bundle is one FIFO slot and a slot that runs long is a
   * head-of-line block: the caller passes the cy window it actually wants (the rows its load set
   * holds for this column), the column's own clip narrows that further, and `maxChunks` caps what
   * is left. Anything the cap leaves behind is not lost — the clip travels with the response, so
   * the streamer can request the remainder through the ordinary per-chunk path on its next pass,
   * with no probe to wait for.
   */
  generateLodColumnBundle(
    cx: number, cz: number, lod: number, wantMinCy: number, wantMaxCy: number, maxChunks: number,
  ): LodColumnBundleResponse {
    const { minCy, maxCy } = this.gen.generateLodColumn(cx, cz, lod);
    const lo = Math.max(minCy, wantMinCy);
    const hi = Math.min(maxCy, wantMaxCy);
    const chunks: Array<{ cy: number; voxelData: Uint32Array }> = [];
    for (let cy = lo; cy <= hi && chunks.length < maxChunks; cy++) {
      chunks.push({ cy, voxelData: this.gen.generateChunkLOD(cx, cy, cz, lod) });
    }
    return { cx, cz, lod, minCy, maxCy, chunks };
  }

  /** Generate a tile with stamp-corrected surface heights (equivalent of a MAP_TILE_DATA response). */
  generateTile(tx: number, tz: number): MapTileResponse {
    const { tile } = this.buildSurfaceColumn(tx, tz);
    return { tx, tz, heights: tile.heights, materials: tile.materials };
  }

  /**
   * Generate a bootstrap surface column: tile + the chunks that intersect the
   * surface. Mirrors SurfaceColumnProvider.generateColumn.
   */
  generateColumn(tx: number, tz: number): SurfaceColumnResponse {
    const { tile, chunks } = this.buildSurfaceColumn(tx, tz);
    return { tx, tz, heights: tile.heights, materials: tile.materials, chunks };
  }
}
